import type { NavTab, NavTabContext } from 'kci-design-lib';

export type ClaraContextEntry = { key: string; value: unknown };

export type WorkspaceTabGrouping = Pick<NavTab, 'clientId' | 'clientLabel' | 'engId' | 'role'>;

export function resolveWorkspaceIdentity(
  tabContext: NavTabContext | null,
  context?: readonly ClaraContextEntry[],
): { workspaceId: string; objectiveId: string } | null {
  const tabWorkspaceId = tabContext?.kind === 'workspace'
    ? tabContext.workspaceId
    : tabContext?.kind === 'chat'
      ? tabContext.workspaceId
      : undefined;
  const tabObjectiveId = tabContext?.kind === 'workspace'
    ? tabContext.objectiveId ?? undefined
    : tabContext?.kind === 'chat'
      ? tabContext.objectiveId
      : undefined;
  const contextValue = (key: string) => {
    const value = context?.find((entry) => entry.key === key)?.value;
    return typeof value === 'string' ? value.trim() : '';
  };
  const workspaceId = tabWorkspaceId?.trim() || contextValue('workspaceId');
  const objectiveId = tabObjectiveId?.trim() || contextValue('objectiveId');

  return workspaceId && objectiveId ? { workspaceId, objectiveId } : null;
}

export function resolveWorkspaceTabGrouping(
  tabContext: NavTabContext | null,
  sourceTab: NavTab | null,
  context?: readonly ClaraContextEntry[],
): WorkspaceTabGrouping | null {
  const identity = resolveWorkspaceIdentity(tabContext, context);
  if (!identity) return null;

  return {
    clientId: sourceTab?.clientId ?? identity.workspaceId,
    clientLabel: sourceTab?.clientLabel ?? identity.workspaceId,
    engId: sourceTab?.engId ?? identity.workspaceId,
    role: 9,
  };
}

export function resolveTabClaraContext(
  tabContext: NavTabContext | null,
  override?: readonly ClaraContextEntry[],
): ClaraContextEntry[] {
  if (override?.length) {
    return [...override];
  }

  if (tabContext?.kind === 'workspace') {
    if (!tabContext.workspaceId || !tabContext.objectiveId) return [];
    return [
      { key: 'workspaceId', value: tabContext.workspaceId },
      { key: 'objectiveId', value: tabContext.objectiveId },
      ...(tabContext.objectiveType
        ? [{ key: 'objectiveType', value: tabContext.objectiveType }]
        : []),
      ...(tabContext.objectiveCategory
        ? [{ key: 'objectiveCategory', value: tabContext.objectiveCategory }]
        : []),
    ];
  }

  if (tabContext?.kind === 'chat' && tabContext.workspaceId && tabContext.objectiveId) {
    return [
      { key: 'workspaceId', value: tabContext.workspaceId },
      { key: 'objectiveId', value: tabContext.objectiveId },
    ];
  }

  return [];
}